import React from 'react';
import { Tag as StandardTag } from '@emorg-prd/standard-react';
import './Tag.css';

export type TagSize = 'default' | 'small' | 'medium' | 'large';

interface TagProps extends React.ComponentProps<typeof StandardTag> {
	size?: TagSize;
}

/**
 * Wraps the standard Tag component.
 * When `onClick` is provided without `closing`, renders the tag as clickable
 * on the whole element (the standard component only wires onClick to the X icon).
 * When `closing` is true the standard behaviour (X icon dismisses the tag) is preserved.
 */
export const Tag: React.FC<TagProps> = ({ onClick, closing, optionalClass, color, label, avatarSrc, size = 'default', ...rest }) => {
	const itemClass = [size !== 'default' && `custom-tag--${size}`, optionalClass].filter(Boolean).join(' ');
	// Standard Tag only fires onClick via the closing (X) icon.
	// When the caller wants a clickable tag without the X icon we render the
	// underlying HTML ourselves and attach onClick to the link span.
	if (onClick && !closing) {
		return (
			<li className={`em-c-tags__item${itemClass ? ` ${itemClass}` : ''}`} {...rest}>
				<span
					className={`em-c-tags__link${color ? ` ${color}` : ''} pointer`}
					tabIndex={0}
					onClick={onClick}
					onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
					role="button"
				>
					{avatarSrc && <img src={avatarSrc} alt="Selected person's avatar" className="em-c-avatar" />}
					<span>{label}</span>
				</span>
			</li>
		);
	}

	return <StandardTag closing={closing} color={color} label={label} avatarSrc={avatarSrc} onClick={onClick} optionalClass={itemClass || undefined} {...rest} />;
};
